'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listAdminHighlightSources,
  updateAdminHighlightSource,
  type AdminHighlightSourceList,
  type UpdateHighlightSourceInput,
} from '@nomiss/services';

export const adminHighlightSourceQueryKeys = {
  all: ['admin', 'highlight-sources'] as const,
  list: () => [...adminHighlightSourceQueryKeys.all, 'list'] as const,
};

export function useAdminHighlightSources(options?: { enabled?: boolean }) {
  const adminHighlightSourcesQuery = useQuery({
    queryKey: adminHighlightSourceQueryKeys.list(),
    queryFn: () => listAdminHighlightSources(),
    enabled: options?.enabled ?? true,
  });

  return { adminHighlightSourcesQuery };
}

export function useUpdateAdminHighlightSource() {
  const queryClient = useQueryClient();

  const updateAdminHighlightSourceMutation = useMutation({
    mutationFn: ({
      leagueId,
      input,
    }: {
      leagueId: string;
      input: UpdateHighlightSourceInput;
    }) => updateAdminHighlightSource(leagueId, input),
    onSuccess: (updated, { leagueId }) => {
      queryClient.setQueryData<AdminHighlightSourceList>(
        adminHighlightSourceQueryKeys.list(),
        (current) => {
          if (!current) return current;
          return {
            sports: current.sports.map((sport) => ({
              ...sport,
              leagues: sport.leagues.map((league) =>
                league.id === leagueId ? { ...league, ...updated } : league,
              ),
            })),
          };
        },
      );
      void queryClient.invalidateQueries({
        queryKey: adminHighlightSourceQueryKeys.list(),
      });
    },
  });

  return { updateAdminHighlightSourceMutation };
}
